import { createSlice } from "@reduxjs/toolkit";
import './../utilities/types' // pulls jsdoc defintions
import { filterOptionTypes } from "../utilities/genericUtils";

/**
 * @type {leadFilterOptions}
 */
const initData = {
        date_assigned: {
            start: -1,
            end: -1,
            field: 'date_assigned',
            active: false,
            excluded: false,
            filterOptionType: filterOptionTypes.RANGE,
        },
        lead_age: {
            start: -1,
            end: -1,
            field: 'borrower_age',
            active: false,
            excluded: false,
            filterOptionType: filterOptionTypes.RANGE,
        },
        state: {
            list: [],
            field: 'state',
            active: false,
            excluded: false,
            filterOptionType: filterOptionTypes.LIST,
        },
        last_contacted: {
            start: -1,
            end: -1,
            field: 'last_contacted',
            active: false,
            excluded: false,
            filterOptionType: filterOptionTypes.RANGE,
        },
        never_contacted: {
            start: 0,
            end: 0,
            field: 'never_contacted',
            active: false,
            excluded: false,
            filterOptionType: filterOptionTypes.RANGE,
        },
        amount_contacted: {
            start: -1,
            end: -1,
            field: 'amount_contacted', // verify that this is correct
            active: false,
            excluded: false,
            filterOptionType: filterOptionTypes.RANGE,
        },
        tags: {
            list: [],
            field: 'tags',
            active: false,
            excluded: false,
            filterOptionType: filterOptionTypes.LIST,
        },
        lead_type: {
            list: [],
            field: 'lead_type',
            active: false,
            excluded: false,
            filterOptionType: filterOptionTypes.LIST,
        },
        lead_level: {
            list: [],
            field: 'lead_level',
            active: false,
            excluded: false,
            filterOptionType: filterOptionTypes.LIST,
        },
        lead_status: {
            list: [],
            field: 'lead_status',
            active: false,
            excluded: false,
            filterOptionType: filterOptionTypes.LIST,
        },
        activeFilterCount: 1,
        save_filters: false,
        labels:{},
        predefined_filter:{
            name: "Active & New",
            params: {
                type: "Active New",
                filterType : 'initialLoad'
            }
        },
        jump_into_queue: false,
        pending_filters: false,
        update_table: false
}

const listFields = ['state','tags','lead_type','lead_level','lead_status']
const rangeFields = ['date_assigned','lead_age','last_contacted','never_contacted','amount_contacted']
const filterNames = ['date_assigned','lead_age','state','last_contacted','amount_contacted','tags','lead_type','lead_level','lead_status','never_contacted']

export const leadTableFiltersSlice = createSlice({
    name: "leadTableFilter",
    initialState: initData,
    reducers: {
        /**
         *
         * @param {WritableDraft<leadFilterOptions>} state
         * @param {listFilterAction} action
         * @returns
         */
        updateListFilter(state, action) {
            const { field, list } = action.payload

            if(!state[field] || !listFields.includes(field)){
                if(process.env.REACT_APP_ENV && process.env.REACT_APP_ENV !== 'production'){
                    console.error('leadTableFiltersSlice::updateListFilter - Bad field type/name', {field, list})
                }
                return
            }

            if(!Array.isArray(list)){
                if(process.env.REACT_APP_ENV && process.env.REACT_APP_ENV !== 'production'){
                    console.error('leadTableFiltersSlice::updateListFilter - Not an array', {field, list})
                }

                return
            }

            state[field].list = list
            state[field].active = true
            state.pending_filters = true
            state.activeFilterCount = state.activeFilterCount + 1
        },

        /**
         *
         * @param {WritableDraft<leadFilterOptions>} state
         * @param {rangeFilterAction} action
         * @returns
         */
        updateRangeFilter(state, action) {
            const { field, start, end } = action.payload

            if(!state[field] || !rangeFields.includes(field)){
                if(process.env.REACT_APP_ENV && process.env.REACT_APP_ENV !== 'production'){
                    console.error('leadTableFiltersSlice::updateRangeFilter - Bad field type/name', { field, start, end })
                }

                return
            }

            state[field].start = start
            state[field].end = end
            state[field].active = true

            state.activeFilterCount = [state.date_assigned, state.lead_age, state.state, state.last_contacted, state.never_contacted, state.amount_contacted, state.tags, state.lead_type, state.lead_level, state.lead_status].filter(f => f.active).length
        },
        resetFilterOption(state, action){
            const {field} = action.payload

            if(!state[field]){
                if(field === 'borrower_age'){
                    state.lead_age = initData.lead_age
                    state.activeFilterCount = [state.date_assigned, state.lead_age, state.state, state.last_contacted, state.never_contacted, state.amount_contacted, state.tags, state.lead_type, state.lead_level, state.lead_status].filter(f => f.active).length
                }
                return
            }

            state[field] = initData[field]
            state.activeFilterCount = [state.date_assigned, state.lead_age, state.state, state.last_contacted, state.never_contacted, state.amount_contacted, state.tags, state.lead_type, state.lead_level, state.lead_status].filter(f => f.active).length
        },
        updateFilterExclusion(state, action){
            const {field} = action.payload

            if(!state[field]){
                if(field === 'borrower_age'){
                    state.lead_age.excluded = !state.lead_age.excluded
                }
                return
            }

            state[field].excluded = !state[field].excluded
        },
        updateAllFilterExclusion(state, action){
            const {exclude} = action.payload

            for (let index = 0; index < filterNames.length; index++) {
                const field = filterNames[index];

                if(state[field].active === true){
                    state[field].excluded = exclude
                }
            }
            state.pending_filters = true
        },
        removeAllFilters(state){
            for (let index = 0; index < filterNames.length; index++) {
                const field = filterNames[index];

                if(state[field].active === true){
                    state[field] = initData[field]
                }
            }

            state.activeFilterCount = 0
        },
        captureCurrentFilters(state, action){
            state.save_filters = !!action.payload
        },
        updateLabels(state, action){
            const { field, label } = action.payload
            state.labels[field] = label
        },

        /**
         *
         * @param {WritableDraft<leadFilterOptions>} state
         * @param {PreDefinedFilterAction} action
         * @returns
         */
        updatePredefinedFilter(state, action){
            const {name, params} = action.payload

            state.predefined_filter.name = name
            state.predefined_filter.params = params
        },

        toggleJumpIntoQueue(state, action){
            state.jump_into_queue = !!!action.payload
        },

        updatePendingFilters(state, action){
            state.pending_filters = !!action.payload // ensure that the assigned value is still a bool
        },

        resetPredefinedFilter(state){
            state.predefined_filter = initData.predefined_filter
        },

        setTableUpdate(state, action){
            state.update_table = !!action.payload
        }
    }
})

export const { updateListFilter, updateRangeFilter, resetFilterOption, updateFilterExclusion, updateAllFilterExclusion, removeAllFilters, captureCurrentFilters, updateLabels, updatePredefinedFilter, toggleJumpIntoQueue, updatePendingFilters, resetPredefinedFilter, setTableUpdate } = leadTableFiltersSlice.actions
export default leadTableFiltersSlice.reducer